import { Copy, ExternalLink, Eye, Lock, QrCode, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import StatusBadge from "../_shared/StatusBadge"; // Importiere StatusBadge

interface LinkEintragProps {
	kuerzel: string;
	originalUrl: string;
	aufrufe: number;
	hatPasswort?: boolean;
	istBesitzer: boolean;
	onLinkKlick: (kuerzel: string) => void;
	onQrAnzeigen: (kuerzel: string) => void;
	onEditPassword: (kuerzel: string) => void;
}

export function LinkEintrag({
	kuerzel,
	originalUrl,
	aufrufe,
	hatPasswort,
	istBesitzer,
	onLinkKlick,
	onQrAnzeigen,
	onEditPassword,
}: LinkEintragProps) {
	const baseUrl = window.location.origin;
	const fullUrl = `${baseUrl}/link/${kuerzel}`;
	const shortCode = `/link/${kuerzel}`;

	// Keine Ablaufzeit mehr

	return (
		<div className="mb-0 flex flex-col items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 p-3 md:flex-row dark:border-gray-700 dark:bg-gray-800">
			<div className="min-w-0 flex-1">
				{/* Hauptlink - bleibt vorerst unverändert */}
				<button
					className={`flex max-w-full items-center gap-2 overflow-x-auto rounded-lg px-2 py-1 font-medium font-mono text-lg transition-all ${
						fullUrl
							? "group cursor-pointer text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
							: "cursor-not-allowed text-gray-400 dark:text-gray-500"
					}`}
					disabled={!fullUrl}
					onClick={() => onLinkKlick(kuerzel)}
					title="Klicken zum Kopieren"
					type="button"
				>
					<span className="truncate">
						{baseUrl}
						<span className="text-blue-600 dark:text-blue-400">
							{shortCode}
						</span>
					</span>
					<Copy className="h-5 w-5 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-500" />
				</button>

				{/* Original + Views in einer Zeile */}
				<div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
					<div className="inline-flex items-center rounded-full border border-gray-100 bg-white px-3 py-1 dark:border-gray-700 dark:bg-gray-700/50">
						<span className="mr-1 font-medium text-gray-500 dark:text-gray-400">
							Original:
						</span>
						<button
							className="max-w-48 truncate text-blue-600 hover:underline dark:text-blue-400"
							onClick={() => window.open(originalUrl, "_blank")}
							title="Original-Link öffnen"
							type="button"
						>
							{originalUrl}
						</button>
					</div>
					<div className="inline-flex items-center rounded-full border border-gray-100 bg-white px-3 py-1 dark:border-gray-700 dark:bg-gray-700/50">
						<Eye className="mr-1 h-3.5 w-3.5 text-gray-600 dark:text-gray-300" />
						<span className="text-gray-600 dark:text-gray-300">
							{aufrufe}x angezeigt
						</span>
					</div>
					{hatPasswort && (
						<StatusBadge color="amber" icon={Lock} text="Geschützt" />
					)}
				</div>
			</div>

			{/* Rechte Seite: Aktionsbuttons */}
			<div className="flex items-center gap-2 md:flex-col">
				<div className="flex gap-1">
					{istBesitzer && (
						<Button
							className="h-8 w-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
							onClick={() => onEditPassword(kuerzel)}
							size="icon"
							title="Passwort bearbeiten"
							type="button"
							variant="ghost"
						>
							<Settings className="h-4 w-4" />
						</Button>
					)}
					<Button
						className="h-8 w-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
						onClick={() => onQrAnzeigen(kuerzel)}
						size="icon"
						title="QR-Code anzeigen"
						type="button"
						variant="ghost"
					>
						<QrCode className="h-4 w-4" />
					</Button>
					<Button
						className="h-8 w-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
						onClick={() => window.open(fullUrl, "_blank")}
						size="icon"
						title="Gekürzten Link öffnen"
						type="button"
						variant="ghost"
					>
						<ExternalLink className="h-4 w-4" />
					</Button>
				</div>
			</div>
		</div>
	);
}
